import type { AiCapabilityPage } from "../schema.js";
import { LIVE, OWNER_ADMIN } from "./_shared.js";

/**
 * `COL-EDIT` (@UC0216) — trang **redirect** (US-AIV2-0049): chat KHÔNG hỏi ô, KHÔNG có nút Đồng ý,
 * chỉ một câu giải thích + nút mở đúng màn hình `/groups/:groupId/collections` (ADR-0009 bất biến 5).
 * US-AIV2-0110 S10 (`@UC0259`): trước trỏ trang chủ nhóm `/groups/:groupId`; nay tab "Quản lý quỹ" (danh sách đợt thu) — màn chi tiết đợt thu cần `collectionId` mà engine chưa điền được.
 * Cửa mobile trang này chịu trách nhiệm (QĐ PO 17/09 mục 8 — sửa đợt thu đang mở, tạm redirect).

 * 🔴 Vai HẸP HƠN cảm giác ban đầu (US-AIV2-0049, review #827 H1 — ĐO từ guard thật, đừng nới lại):
 *   `PATCH /funds/{fundId}/collections/{collectionId}` gác `@RequireFundRoles(OWNER, ADMIN)` — thủ quỹ sửa đợt thu là 403.
 *   Trang redirect chỉ mở màn hình, nên mời sai vai = dẫn người ta tới nút bấm vào là 403
 *   (CLAUDE.md §4 cấm: "dẫn member thường vào form quản trị rồi để 403 ở bước chốt").
 *   Khoá bằng `roles-parity.invariants.spec.ts` — nới ở đây mà GUI không nới thì test ĐỎ.
 */
export const COL_EDIT = {
  id: "COL-EDIT",
  uc: "@UC0216",
  tier: "redirect",
  status: LIVE,
  roles: OWNER_ADMIN,
  flags: [],
  slots: [],
  llm: {
    description: "Sửa một đợt thu đang mở: đổi tên, số tiền mỗi người hoặc hạn chót.",
    examples: [
      "sửa đợt thu",
      "sua dot thu",
      "đổi hạn đóng của đợt này",
      "doi han dong cua dot nay",
      "sửa số tiền đợt thu",
      // ——— 0105: thêm cho đủ 40 ———
      "sua so tien dot thu",
      "đổi tên đợt thu",
      "doi ten dot thu",
      "sửa lại đợt thu đang mở",
      "sua lai dot thu dang mo",
      "muốn tăng tiền đợt thu lên",
      "muon tang tien dot thu len",
      "giảm tiền đóng của đợt này xuống",
      "giam tien dong cua dot nay xuong",
      "gia hạn đợt thu thêm mấy ngày",
      "gia han dot thu them may ngay",
      "lùi hạn đóng lại",
      "lui han dong lai",
      "đợt thu này ghi sai tên, sửa giúp",
      "dot thu nay ghi sai ten sua giup",
      "chỉnh lại số tiền mỗi người của đợt thu",
      "chinh lai so tien moi nguoi cua dot thu",
      "sửa nội dung đợt thu",
      "sua noi dung dot thu",
      "đổi hạn chót đợt thu tháng này",
      "doi han chot dot thu thang nay",
      "tôi đặt nhầm số tiền đợt thu rồi",
      "toi dat nham so tien dot thu roi",
      "sửa mô tả đợt thu",
      "sua mo ta dot thu",
      "cập nhật đợt thu",
      "cap nhat dot thu",
      "đổi thông tin đợt thu đang chạy",
      "doi thong tin dot thu dang chay",
      "chỉnh đợt thu",
      "chinh dot thu",
      "sửa hạn nộp",
      "sua han nop",
      "đợt thu ghi 200k mà đúng ra là 250k",
      "dot thu ghi 200k ma dung ra la 250k",
      // AI-INTERP C4/C5 (@UC0195): «đổi … THÀNH <số>» của đợt ĐANG MỞ là SỬA đợt, không mở đợt mới (COL-EQUAL hạng 1 trước 23/10).
      "đổi tiền đợt thu này thành 300k",
      "doi tien dot thu nay thanh 300k",
      "sửa đợt đang mở thành 250k mỗi người",
      "sua dot dang mo thanh 250k moi nguoi",
      "đợt thu đang chạy đổi lại số tiền",
      "dot thu dang chay doi lai so tien",
      // Câu SỬA cũng nói số tiền như câu MỞ: thiếu số trong ví dụ thì BM25 chấm số («150k», «200k») cho riêng COL-EQUAL (đo 23/10: «150k» 3,21 vs 0).
      "đợt tháng này ghi 200k, sửa lại thành 150k",
      "dot thang nay ghi 200k sua lai thanh 150k",
      "nâng mức đóng đợt đang mở lên 350k",
      "nang muc dong dot dang mo len 350k",
      "hạ mức đóng của đợt thu xuống",
      "ha muc dong cua dot thu xuong",
    ],
    // AI-INTERP M12 (QĐ I12): câu THỬ — KHÔNG vào prompt/BM25; viết đời thường, không chép ví dụ (validator HELD_OUT_IS_EXAMPLE).
    heldOutExamples: [
      "dot thu nay doi thanh 150k dc ko",
      "sửa giùm em đợt quỹ tháng 11 thành 180k mỗi bé nha",
      "lỡ ghi 200 mà đúng là 150k, chỉnh đợt thu lại sao chị",
      "doi ten dot thu hk1 lai cho dung di em",
      "dời hạn đóng đợt này qua cuối tháng dc hok",
      "đợt thu đang mở ghi nhầm số tiền rồi, sửa sao z",
    ],
  },
  screen: "/groups/:groupId/collections",
  screenShows: "collection_edit",
  endpoints: [
    "PATCH /funds/{fundId}/collections/{collectionId}",
  ],
  facts: [],
  templates: {
    /*
     * 🔴 GUIDE-SCREEN-TRUTH (lô review 15/10 G-1, @UC0216): màn sửa đợt (`edit_collection_screen.dart`) chỉ có ô Tên + Mô tả, và cửa
     * `PATCH …/collections/:id` nhận đúng hai trường đó (`updateCollectionInfoRequestSchema`) — câu cũ ngầm hứa sửa số tiền/hạn ⇒ "gia hạn
     * đợt thu" bấm mãi không xong. Lối ra CÓ THẬT trên app cho đúng vai (chủ nhóm · quản trị): nút ngừng đợt ở chính màn sửa (`close` — giữ
     * nguyên mọi khoản đã ghi) + mở đợt mới. `llm.*` cố ý KHÔNG đổi (0 ca eval cũ đi) — nợ ghi ở PR.
     */
    redirect:
      "Mình mở danh sách đợt thu nhé — bấm vào đợt cần sửa rồi bấm nút sửa: ở đó đổi được tên và mô tả. Số tiền mỗi người và hạn chót của đợt đã mở thì app chưa cho đổi; cần khác thì ngừng đợt này (nút ngay trong màn sửa) rồi mở đợt mới — những khoản đã ghi ở đợt cũ vẫn giữ nguyên.",
  },
  journey: { after: ["READ-COLLECTIONS"] },
  help: { title: "Sửa đợt thu", tryNow: "sửa đợt thu" },
} satisfies AiCapabilityPage;
